import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Employer } from 'src/app/models/employer';
import { Interaction } from 'src/app/models/interaction';
import { AdminService } from 'src/app/services/admin.service';
import { DataService } from 'src/app/services/data.service';
import { SessionService } from 'src/app/services/session.service';
import { SiteService } from 'src/app/services/site.service';

@Component({
  selector: 'app-user-template',
  templateUrl: './user-template.component.html',
  styleUrls: ['./user-template.component.scss']
})
export class UserTemplateComponent implements OnInit, OnDestroy {

	public activeLink: string = 'home';
	public path: string;
	public menuOpen: boolean = false;
	public logo: string = '';
	public employer: Employer = null;
	public socialLinks: any = {};
	// Branding overrides (content category 9). Empty = standard header/footer.
	public headerLogoOverride: string = '';
	public footerLogoOverride: string = '';
	private entryLogged: boolean = false;

	/*
	 * Page View gate (2026-09-16).
	 *
	 * The Page View engagement (type 6) used to be sent the moment the employer
	 * resolved, i.e. on component load. Nothing human was required, so every
	 * headless render of the site produced one -- and corporate email security
	 * (Safe Links, Proofpoint, Mimecast) renders the link in every delivered email.
	 * Premier Health, 2026-08-25: 8,329 "page views" in 11 hours, 1 real person.
	 *
	 * Now the page view is held until the first sign of a person:
	 *   - any pointer / click / scroll / wheel / touch / key event, or
	 *   - the tab has been VISIBLE for a cumulative PAGE_VIEW_DWELL_MS (10 s).
	 * A scanner renders, snapshots and closes in under two seconds without moving a
	 * pointer, so it never sends the row. A person who only reads still trips the
	 * dwell timer. 10 s is Google Analytics 4's engaged-session threshold.
	 * Automated renders that announce themselves (navigator.webdriver, speculative
	 * prerender) are never armed at all.
	 *
	 * Interactions (types 1-5, interact.directive.ts) are unaffected: they are bound
	 * to real DOM events already. A click that fires one also trips this gate, so
	 * the page view is recorded alongside it.
	 */
	static readonly PAGE_VIEW_DWELL_MS = 10000;
	static readonly PAGE_VIEW_TICK_MS = 1000;
	static readonly HUMAN_EVENTS: string[] = ['pointermove', 'pointerdown', 'click', 'scroll', 'wheel', 'touchstart', 'keydown'];
	private pendingPageView: Interaction | null = null;
	private gateListeners: Array<{ type: string; handler: EventListener }> = [];
	private dwellTimer: any = null;
	private visibleMs: number = 0;

	constructor(private route: ActivatedRoute, private router: Router, private siteService: SiteService, private sessionService: SessionService, private dataService: DataService, private adminService: AdminService) { }

	ngOnInit(): void {

		this.route.firstChild.data.subscribe(d => {
			this.activeLink = d?.activeLink;
		})
		this.route.firstChild.paramMap.subscribe(p => {
			this.path = p.get('path')
		});
		this.router.events.subscribe(e => {
			this.menuOpen = false;
			if(e.type == 11) {
				this.activeLink = e.snapshot.firstChild.data?.activeLink;
			}
		});
		this.siteService.onEmployerSet.subscribe((e:Employer) => {
			this.employer = e;
			if(e) {
				this.sessionService.employerID = e.employerID;
				this.logo = '"' + e.logo + '"';
				const branding = e.employerContent?.contentCategories?.find(c => c.contentCategoryID === 9);
				this.headerLogoOverride = branding?.content?.find(i => i.contentDesc === 'Header Logo Override')?.fileContent || '';
				this.footerLogoOverride = branding?.content?.find(i => i.contentDesc === 'Footer Logo Override')?.fileContent || '';
				if(!this.adminService.token && !this.entryLogged) {
					this.entryLogged = true;
					// Capture the landing resource now; it is sent later, once a person shows up.
					this.armPageViewGate({
						userCookieID: this.sessionService.visitorID,
						webSessionID: this.sessionService.sessionID,
						employerID: e.employerID,
						engagementTypeID: 6,
						resource: '/' + this.activeLink + '/',
					});
				}
			}
		});

		this.dataService.getSocialMediaLinks().subscribe((links: any) => {
			this.socialLinks = links;
		});

		this.siteService.onPageInitDone.subscribe(() => {
		 	this.setUpObserver()
		});

	}

	ngOnDestroy(): void {
		this.disarmPageViewGate();
	}

	/** Headless / speculative renders that identify themselves are never counted. */
	isAutomatedRender(): boolean {
		const nav: any = typeof navigator !== 'undefined' ? navigator : {};
		const doc: any = typeof document !== 'undefined' ? document : {};
		return nav.webdriver === true || doc.prerendering === true;
	}

	armPageViewGate(pageView: Interaction): void {
		if(this.isAutomatedRender()) { return; }
		this.pendingPageView = pageView;
		this.visibleMs = 0;

		const onHuman: EventListener = () => this.recordPageView();
		for(const type of UserTemplateComponent.HUMAN_EVENTS) {
			document.addEventListener(type, onHuman, { passive: true, capture: true });
			this.gateListeners.push({ type, handler: onHuman });
		}

		this.dwellTimer = setInterval(() => {
			if(document.visibilityState !== 'visible') { return; }
			this.visibleMs += UserTemplateComponent.PAGE_VIEW_TICK_MS;
			if(this.visibleMs >= UserTemplateComponent.PAGE_VIEW_DWELL_MS) {
				this.recordPageView();
			}
		}, UserTemplateComponent.PAGE_VIEW_TICK_MS);
	}

	/** Sends the held page view exactly once and tears the gate down. */
	recordPageView(): void {
		if(!this.pendingPageView) { return; }
		const pageView = this.pendingPageView;
		this.pendingPageView = null;
		this.disarmPageViewGate();
		this.dataService.logInteraction(pageView).subscribe();
	}

	disarmPageViewGate(): void {
		for(const l of this.gateListeners) {
			document.removeEventListener(l.type, l.handler, { capture: true } as any);
		}
		this.gateListeners = [];
		if(this.dwellTimer) {
			clearInterval(this.dwellTimer);
			this.dwellTimer = null;
		}
	}

	setUpObserver() {
		const fadeins = document.querySelectorAll('.fade-in');
		if(!fadeins || fadeins.length == 0) {
			setTimeout(this.setUpObserver, 500);
			return;
		}
		const options: any = { root: null, threshold: 0.2 };
		const observer = new IntersectionObserver((entries, observer) => {
			entries.forEach(entry => {
				if(!entry.isIntersecting) { return; }
				entry.target.classList.add('appear');
				observer.unobserve(entry.target);
			});
		}, options);

		fadeins.forEach((entry:any) => {
			observer.observe(entry);
		});


	}
}
